import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTour } from '../context/TourContext';
import { highlightPulseVariants } from '../lib/motion';
import { SpringConfigs } from '../lib/motion';

const TOUR_STORAGE_KEY = 'helios_tour_complete';

// eslint-disable-next-line react-refresh/only-export-components
export function setTourComplete(): void {
  try { localStorage.setItem(TOUR_STORAGE_KEY, 'true'); } catch { /* intentionally empty */ }
}

// eslint-disable-next-line react-refresh/only-export-components
export function isTourComplete(): boolean {
  try { return localStorage.getItem(TOUR_STORAGE_KEY) === 'true'; } catch { return false; }
}

export default function AppTour() {
  const { tourActive, currentStep, currentStepData, totalSteps, nextStep, skipTour, finishTour } = useTour();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  const isFinal = currentStep === totalSteps - 1;

  const updateTargetRect = useCallback(() => {
    if (!currentStepData?.targetElement) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(currentStepData.targetElement);
    if (el) {
      setTargetRect(el.getBoundingClientRect());
    } else {
      setTargetRect(null);
    }
  }, [currentStepData?.targetElement]);

  useEffect(() => {
    if (!tourActive) return;
    updateTargetRect();
    const raf = requestAnimationFrame(updateTargetRect);
    const resize = () => updateTargetRect();
    window.addEventListener('resize', resize);
    const interval = setInterval(updateTargetRect, 300);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      clearInterval(interval);
    };
  }, [tourActive, currentStep, updateTargetRect]);

  // Auto-advance
  useEffect(() => {
    if (!tourActive || !currentStepData?.autoAdvanceMs) return;
    const t = setTimeout(nextStep, currentStepData.autoAdvanceMs);
    return () => clearTimeout(t);
  }, [tourActive, currentStep, currentStepData, nextStep]);

  if (!tourActive || !currentStepData) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, pointerEvents: 'none' }}>
      {/* Backdrop */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }} aria-hidden>
        {(!targetRect || isFinal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)' }}
          />
        )}
        {targetRect && !isFinal && (
          <>
            {/* Spotlight cutout */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed',
                left: targetRect.left,
                top: targetRect.top,
                width: targetRect.width,
                height: targetRect.height,
                borderRadius: '8px',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.85)',
              }}
            />
            {/* Gold highlight pulse */}
            <motion.div
              variants={highlightPulseVariants}
              animate="pulse"
              style={{
                position: 'fixed',
                left: targetRect.left - 4,
                top: targetRect.top - 4,
                width: targetRect.width + 8,
                height: targetRect.height + 8,
                borderRadius: 12,
                pointerEvents: 'none',
              }}
            />
          </>
        )}
      </div>

      {/* Tour card */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '16px 16px 32px', pointerEvents: 'none' }}>
        <div style={{ width: '100%', maxWidth: '360px', pointerEvents: 'auto' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', ...SpringConfigs.smooth }}
              style={{
                background: 'var(--color-surface-ephemeral)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                padding: '20px',
                boxShadow: 'var(--shadow-ephemeral)',
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                  {currentStep + 1} / {totalSteps}
                </span>
                <button type="button" onClick={skipTour} style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: '10px', cursor: 'pointer', fontWeight: 400 }}>
                  Skip tour
                </button>
              </div>

              {/* Title — staggered line reveal */}
              <motion.h2
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '8px' }}
              >
                {currentStepData.title}
              </motion.h2>

              {/* Body */}
              <motion.p
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                style={{ fontSize: '13px', color: 'var(--color-text-secondary)', fontWeight: 400, lineHeight: 1.5, marginBottom: '16px' }}
              >
                {currentStepData.body}
              </motion.p>

              {/* Step dots */}
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginBottom: '16px' }}>
                {Array.from({ length: totalSteps }, (_, i) => (
                  <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', background: i === currentStep ? 'var(--color-cinder)' : 'rgba(255,255,255,0.2)' }} />
                ))}
              </div>

              {/* Buttons */}
              {isFinal ? (
                <motion.button
                  type="button"
                  onClick={finishTour}
                  whileHover={{ scale: 1.02, boxShadow: '0 0 12px rgba(232,160,32,0.6)' }}
                  whileTap={{ scale: 0.98 }}
                  transition={SpringConfigs.micro}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    background: 'var(--color-cinder)',
                    border: 'none',
                    color: 'var(--color-canvas)',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Done
                </motion.button>
              ) : (
                <motion.button
                  type="button"
                  onClick={nextStep}
                  whileHover={{ scale: 1.02, boxShadow: '0 0 12px rgba(232,160,32,0.6)' }}
                  whileTap={{ scale: 0.98 }}
                  transition={SpringConfigs.micro}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    background: 'var(--color-cinder)',
                    border: 'none',
                    color: 'var(--color-canvas)',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {currentStepData.autoAdvanceMs ? 'Got it' : 'Next'}
                </motion.button>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
